import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { chartOfAccountsService } from '../../../services/accounting/chart-of-accounts';
import toast from 'react-hot-toast';

const CategoryManagementModal = ({ isOpen = true, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [newCategory, setNewCategory] = useState({
    name: '',
    nameAr: '',
    icon: '',
    color: 'blue',
    accountCode: ''
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIconDropdown, setShowIconDropdown] = useState(false);
  const [showEditIconDropdown, setShowEditIconDropdown] = useState(false);

  const colorOptions = [
    { value: 'blue', label: 'Blue', class: 'bg-blue-500' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-500' },
    { value: 'green', label: 'Green', class: 'bg-green-500' },
    { value: 'yellow', label: 'Yellow', class: 'bg-yellow-500' },
    { value: 'gray', label: 'Gray', class: 'bg-gray-500' },
    { value: 'indigo', label: 'Indigo', class: 'bg-indigo-500' },
    { value: 'sky', label: 'Sky', class: 'bg-sky-500' },
    { value: 'emerald', label: 'Emerald', class: 'bg-emerald-500' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-500' },
    { value: 'red', label: 'Red', class: 'bg-red-500' },
    { value: 'cyan', label: 'Cyan', class: 'bg-cyan-500' },
    { value: 'amber', label: 'Amber', class: 'bg-amber-500' },
    { value: 'teal', label: 'Teal', class: 'bg-teal-500' },
    { value: 'slate', label: 'Slate', class: 'bg-slate-500' }
  ];

  const iconCategories = {
    business: ['🏢', '💼', '📊', '📈', '💻', '📱', '🖥️', '📞', '📧', '📋'],
    finance: ['💰', '💳', '🏦', '📈', '💵', '💸', '🪙', '💎', '🏆', '⭐'],
    transport: ['🚗', '🚚', '🚛', '🚌', '✈️', '🚢', '🚅', '🚲', '🛵', '🚁'],
    services: ['🔧', '🔨', '⚡', '🚿', '🛢️', '🔌', '💡', '🔋', '🛠️', '⚙️'],
    communication: ['📢', '📣', '📡', '📻', '📺', '📱', '💬', '📞', '📧', '🌐'],
    legal: ['⚖️', '📜', '🔒', '🛡️', '👨‍⚖️', '📋', '✍️', '📝', '🔐', '🎯'],
    travel: ['✈️', '🏨', '🗺️', '🧳', '🎫', '🚗', '🚢', '🚁', '🎪', '🏖️'],
    maintenance: ['🔧', '🔨', '🛠️', '⚙️', '🔌', '💡', '🔋', '🛢️', '🚿', '🧰'],
    food: ['🍽️', '🍕', '🍔', '☕', '🍺', '🍷', '🍰', '🍫', '🍎', '🥗'],
    health: ['🏥', '💊', '🩺', '🩹', '🩻', '🧬', '🔬', '💉', '🦷', '👁️'],
    education: ['📚', '🎓', '✏️', '📝', '📖', '🎒', '🏫', '👨‍🏫', '📊', '🔬'],
    entertainment: ['🎬', '🎮', '🎵', '🎨', '🎭', '🎪', '🎯', '🎲', '🎸', '🎹']
  };

  const allIcons = Object.values(iconCategories).flat();

  const loadAccounts = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    try {
      // Try to get accounts, with fallback to initialize if none exist
      let accountsData = await chartOfAccountsService.getChartOfAccounts(currentCompany.id);
      
      // If no accounts exist, initialize default ones
      if (accountsData.length === 0) {
        console.log('No accounts found, initializing default chart of accounts...');
        await chartOfAccountsService.initializeDefaultChartOfAccounts(currentCompany.id, 'system');
        accountsData = await chartOfAccountsService.getChartOfAccounts(currentCompany.id);
      }
      
      setAccounts(accountsData);
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error(t('coa.error.loading_accounts_failed', 'Failed to load accounts'));
      
      // Set empty array as fallback
      setAccounts([]);
    }
  }, [currentCompany?.id, t]);

  const loadCategories = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    try {
      // Use localStorage fallback since expensesService.getExpenseCategories might not exist
      const storedCategories = localStorage.getItem(`expense_categories_${currentCompany.id}`);
      const customCategories = storedCategories ? JSON.parse(storedCategories) : [];
      
      // Combine with default categories
      const defaultCategories = [
        { id: 'office', name: 'Office Expenses', nameAr: 'مصاريف المكتب', icon: '🏢', color: 'blue', isDefault: true },
        { id: 'marketing', name: 'Marketing', nameAr: 'التسويق', icon: '📢', color: 'purple', isDefault: true },
        { id: 'salary', name: 'Salaries', nameAr: 'الرواتب', icon: '💰', color: 'green', isDefault: true },
        { id: 'utility', name: 'Utilities', nameAr: 'المرافق', icon: '⚡', color: 'yellow', isDefault: true },
        { id: 'equipment', name: 'Equipment', nameAr: 'المعدات', icon: '🔧', color: 'gray', isDefault: true },
        { id: 'legal', name: 'Legal', nameAr: 'القانونية', icon: '⚖️', color: 'indigo', isDefault: true },
        { id: 'travel', name: 'Travel', nameAr: 'السفر', icon: '✈️', color: 'sky', isDefault: true },
        { id: 'insurance', name: 'Insurance', nameAr: 'التأمين', icon: '🛡️', color: 'emerald', isDefault: true },
        { id: 'maintenance', name: 'Maintenance', nameAr: 'الصيانة', icon: '🔨', color: 'orange', isDefault: true },
        { id: 'fuel', name: 'Fuel Expenses', nameAr: 'مصاريف الوقود', icon: '⛽', color: 'red', isDefault: true },
        { id: 'truck_washing', name: 'Truck Washing', nameAr: 'غسيل الشاحنات', icon: '🚿', color: 'cyan', isDefault: true },
        { id: 'oil_change', name: 'Oil Changes', nameAr: 'تغيير الزيت', icon: '🛢️', color: 'amber', isDefault: true },
        { id: 'commission', name: 'Commission', nameAr: 'العمولة', icon: '💼', color: 'teal', isDefault: true },
        { id: 'other', name: 'Other', nameAr: 'أخرى', icon: '📋', color: 'slate', isDefault: true }
      ];
      
      setCategories([...defaultCategories, ...customCategories]);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error(t('expenses.error.loading_categories_failed', 'Failed to load categories'));
    }
  }, [currentCompany?.id, t]);

  useEffect(() => {
    if (isOpen && currentCompany?.id) {
      loadAccounts();
      loadCategories();
    }
  }, [isOpen, currentCompany?.id, loadAccounts, loadCategories]);

  const saveCategories = async (updatedCategories) => {
    try {
      const customCategories = updatedCategories.filter(cat => !cat.isDefault);
      localStorage.setItem(`expense_categories_${currentCompany?.id}`, JSON.stringify(customCategories));
      setCategories(updatedCategories);
    } catch (error) {
      console.error('Error saving categories:', error);
      toast.error(t('expenses.categories.save_error', 'Failed to save categories'));
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error(t('expenses.categories.name_required', 'Category name is required'));
      return;
    }

    if (!newCategory.accountCode) {
      toast.error(t('expenses.categories.account_required', 'Account selection is required'));
      return;
    }

    setLoading(true);
    try {
      const selectedAccount = accounts.find(acc => acc.accountCode === newCategory.accountCode);
      const categoryId = `custom_${Date.now()}`;
      const category = {
        id: categoryId,
        name: newCategory.name.trim(),
        nameAr: newCategory.nameAr.trim() || newCategory.name.trim(),
        icon: newCategory.icon || '📋',
        color: newCategory.color,
        accountCode: newCategory.accountCode,
        accountName: selectedAccount?.accountName || '',
        isDefault: false
      };

      const updatedCategories = [...categories, category];
      await saveCategories(updatedCategories);
      
      setNewCategory({ name: '', nameAr: '', icon: '', color: 'blue', accountCode: '' });
      toast.success(t('expenses.categories.added_success', 'Category added successfully'));
    } catch (error) {
      toast.error(t('expenses.categories.add_error', 'Failed to add category'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    if (category.isDefault) {
      toast.error(t('expenses.categories.cannot_edit_default', 'Cannot edit default categories'));
      return;
    }
    setEditingCategory({ ...category });
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory.name.trim()) {
      toast.error(t('expenses.categories.name_required', 'Category name is required'));
      return;
    }

    setLoading(true);
    try {
      const updatedCategories = categories.map(cat => 
        cat.id === editingCategory.id ? editingCategory : cat
      );
      await saveCategories(updatedCategories);
      
      setEditingCategory(null);
      toast.success(t('expenses.categories.updated_success', 'Category updated successfully'));
    } catch (error) {
      toast.error(t('expenses.categories.update_error', 'Failed to update category'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (category?.isDefault) {
      toast.error(t('expenses.categories.cannot_delete_default', 'Cannot delete default categories'));
      return;
    }

    if (!window.confirm(t('expenses.categories.confirm_delete', 'Are you sure you want to delete this category?'))) {
      return;
    }

    setLoading(true);
    try {
      const updatedCategories = categories.filter(cat => cat.id !== categoryId);
      await saveCategories(updatedCategories);
      toast.success(t('expenses.categories.deleted_success', 'Category deleted successfully'));
    } catch (error) {
      toast.error(t('expenses.categories.delete_error', 'Failed to delete category'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    if (onSuccess) onSuccess();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('expenses.categories.manage_title', 'Manage Expense Categories')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Add New Category */}
          <div className="mb-8 p-6 bg-gray-50 rounded-lg border">
            <h3 className="text-lg font-medium mb-6 text-gray-900">
              {t('expenses.categories.add_new', 'Add New Category')}
            </h3>
            
            <div className="space-y-4">
              {/* First row: Name fields and Icon */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> {t('expenses.categories.name_en', 'Name (English)')}
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    placeholder={t('expenses.categories.name_placeholder', 'Enter category name')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('expenses.categories.name_ar', 'Name (Arabic)')}
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={newCategory.nameAr}
                    onChange={(e) => setNewCategory({ ...newCategory, nameAr: e.target.value })}
                    placeholder={t('expenses.categories.name_ar_placeholder', 'Enter Arabic name')}
                  />
                </div>
                
                <div className="flex flex-col items-center">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('expenses.categories.icon', 'Icon')}
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowIconDropdown(!showIconDropdown)}
                      className="w-12 h-12 border border-gray-300 rounded-lg flex items-center justify-center bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-2xl"
                      aria-label={t('expenses.categories.select_icon', 'Select Icon')}
                    >
                      {newCategory.icon ? newCategory.icon : <span className="text-gray-400">📋</span>}
                    </button>
                    {showIconDropdown && (
                      <div className="absolute z-10 w-64 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto left-0">
                        <div className="p-3">
                          <div className="grid grid-cols-8 gap-2">
                            {allIcons.map(icon => (
                              <button
                                key={icon}
                                type="button"
                                className="w-8 h-8 flex items-center justify-center text-lg hover:bg-blue-100 border border-gray-200 rounded transition-colors"
                                onClick={() => {
                                  setNewCategory({ ...newCategory, icon });
                                  setShowIconDropdown(false);
                                }}
                                title={`Select ${icon}`}
                              >
                                {icon}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Second row: Account and Color */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> {t('expenses.categories.account', 'Account')}
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={newCategory.accountCode}
                    onChange={(e) => setNewCategory({ ...newCategory, accountCode: e.target.value })}
                    disabled={accounts.length === 0}
                  >
                    <option value="">
                      {accounts.length === 0 
                        ? t('expenses.categories.loading_accounts', 'Loading accounts...') 
                        : t('expenses.categories.select_account', 'Select Account')
                      }
                    </option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.accountCode}>
                        {account.accountCode} - {account.accountName}
                      </option>
                    ))}
                  </select>
                  {accounts.length === 0 && (
                    <div className="text-sm text-amber-600 mt-1">
                      {t('expenses.categories.no_accounts_warning', 'No accounts found. The system will initialize default accounts automatically.')}
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('expenses.categories.color', 'Color')}
                  </label>
                  <div className="flex items-center space-x-3">
                    <select
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={newCategory.color}
                      onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                    >
                      {colorOptions.map(color => (
                        <option key={color.value} value={color.value}>
                          {color.label}
                        </option>
                      ))}
                    </select>
                    <div 
                      className={`w-8 h-8 rounded-lg border-2 border-white shadow-md ${colorOptions.find(c => c.value === newCategory.color)?.class || 'bg-blue-500'}`}
                      title={`Color preview: ${colorOptions.find(c => c.value === newCategory.color)?.label || 'Blue'}`}
                    ></div>
                  </div>
                </div>
              </div>
              
              {/* Button */}
              <div className="flex justify-end pt-2">
                <button
                  onClick={handleAddCategory}
                  disabled={loading || !newCategory.name.trim() || !newCategory.accountCode}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {loading ? t('common.adding', 'Adding...') : t('expenses.categories.add_button', 'Add Category')}
                </button>
              </div>
            </div>
          </div>

          {/* Categories List */}
          <div>
            <h3 className="text-lg font-medium mb-4">
              {t('expenses.categories.existing_title', 'Existing Categories')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className={`p-4 border rounded-lg ${category.isDefault ? 'bg-gray-50' : 'bg-white'}`}
                >
                  {editingCategory?.id === category.id ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        value={editingCategory.name}
                        onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                        placeholder={t('expenses.categories.name_placeholder', 'Enter category name')}
                      />
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        value={editingCategory.nameAr}
                        onChange={(e) => setEditingCategory({ ...editingCategory, nameAr: e.target.value })}
                        placeholder={t('expenses.categories.name_ar_placeholder', 'Enter Arabic name')}
                      />
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        value={editingCategory.accountCode || ''}
                        onChange={(e) => {
                          const selectedAccount = accounts.find(acc => acc.accountCode === e.target.value);
                          setEditingCategory({ 
                            ...editingCategory, 
                            accountCode: e.target.value,
                            accountName: selectedAccount?.accountName || ''
                          });
                        }}
                      >
                        <option value="">{t('expenses.categories.select_account', 'Select Account')}</option>
                        {accounts.map(account => (
                          <option key={account.id} value={account.accountCode}>
                            {account.accountCode} - {account.accountName}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1 flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => setShowEditIconDropdown(!showEditIconDropdown)}
                            className="w-12 h-12 border border-gray-300 rounded-lg flex items-center justify-center bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-2xl"
                            aria-label={t('expenses.categories.select_icon', 'Select Icon')}
                          >
                            {editingCategory.icon ? editingCategory.icon : <span className="text-gray-400">📋</span>}
                          </button>
                          {showEditIconDropdown && (
                            <div className="absolute z-10 w-64 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto left-0">
                              <div className="p-3">
                                <div className="grid grid-cols-8 gap-2">
                                  {allIcons.map(icon => (
                                    <button
                                      key={icon}
                                      type="button"
                                      className="w-8 h-8 flex items-center justify-center text-lg hover:bg-blue-100 border border-gray-200 rounded transition-colors"
                                      onClick={() => {
                                        setEditingCategory({ ...editingCategory, icon });
                                        setShowEditIconDropdown(false);
                                      }}
                                      title={`Select ${icon}`}
                                    >
                                      {icon}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <select
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={editingCategory.color}
                          onChange={(e) => setEditingCategory({ ...editingCategory, color: e.target.value })}
                        >
                          {colorOptions.map(color => (
                            <option key={color.value} value={color.value}>
                              {color.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleUpdateCategory}
                          disabled={loading}
                          className="flex-1 px-3 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {t('common.save', t('common.save'))}
                        </button>
                        <button
                          onClick={() => setEditingCategory(null)}
                          className="flex-1 px-3 py-2 bg-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-400 transition-colors"
                        >
                          {t('common.cancel', t('common.cancel'))}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{category.icon}</span>
                          <div>
                            <div className="font-medium">{category.name}</div>
                            {category.nameAr && category.nameAr !== category.name && (
                              <div className="text-sm text-gray-500">{category.nameAr}</div>
                            )}
                            {category.accountCode && (
                              <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1">
                                {category.accountCode} - {category.accountName}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={`w-4 h-4 rounded-full bg-${category.color}-500`}></div>
                      </div>
                      {!category.isDefault && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <PencilIcon className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="p-1 text-red-400 hover:text-red-600 transition-colors"
                          >
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      {category.isDefault && (
                        <div className="text-xs text-gray-500">
                          {t('expenses.categories.default_category', 'Default Category')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('common.done', 'Done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagementModal;